import React, {useCallback, useEffect, useState} from 'react';
import {Button, Card, Col, Empty, Grid, message, Modal, Row, Space, Tag, Tooltip, Typography} from 'antd';
import {
    CalendarOutlined,
    DeleteOutlined,
    EditOutlined,
    FolderOutlined,
    PlusOutlined,
    ReloadOutlined,
    UserOutlined
} from '@ant-design/icons';
import {createProject, deleteProject, getProjectList, updateProject} from '../api/projectApi';
import ProjectCreateModal from './ProjectCreateModal';
import ProjectEditModal from './ProjectEditModal';
import ProjectTabs from './project/ProjectTabs';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';
import PermissionGate from './common/PermissionGate';

const { Title, Text } = Typography;
const { confirm } = Modal;

const ProjectList = () => {
  const screens = Grid.useBreakpoint();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getProjectList);
  const canDeleteSingle = canCall(deleteProject);
  const canUpdate = canCall(updateProject);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      if (permLoading) return;
      if (!canRead) { setProjects([]); message.warning('프로젝트 조회 권한이 없습니다.'); return; }
      const res = await getProjectList();
      if (res && Array.isArray(res.data)) {
        setProjects(res.data);
      } else {
        setProjects([]);
        message.warning('프로젝트 응답 형식이 예상과 다릅니다.');
      }
    } catch (error) {
      // 공통 인터셉터에서 처리됨
    } finally {
      setLoading(false);
    }
  }, [permLoading, canRead]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleDelete = (pjtId, projectName) => {
    if (permLoading || !canDeleteSingle) { message.warning('삭제 권한이 없습니다.'); return; }
    confirm({
      title: '프로젝트 삭제',
      content: `"${projectName}" 프로젝트를 삭제하시겠습니까?`,
      okText: '삭제', okType: 'danger', cancelText: '취소',
      onOk: async () => {
        try {
          await deleteProject(pjtId);
          message.success('프로젝트가 삭제되었습니다.');
          setSelectedProject(null);
          fetchProjects();
        } catch (error) { /* 에러 토스트는 공통 인터셉터에서 처리 */ }
      }
    });
  };

  const handleOpenDetail = (project) => { setSelectedProject(project); };
  const handleCreateSuccess = () => { setIsCreateModalOpen(false); fetchProjects(); message.success('프로젝트가 생성되었습니다.'); };
  const handleEditSuccess = () => { setIsEditModalOpen(false); fetchProjects(); message.success('프로젝트가 수정되었습니다.'); };
  const handleCloseDetail = () => setSelectedProject(null);
  const resolvedSelectedProject = selectedProject ? (projects.find(p => p.pjtId === selectedProject.pjtId) || selectedProject) : null;

  const HeaderSection = (
    <div style={{ 
      display:'flex', 
      justifyContent:'space-between', 
      alignItems: screens.xs ? 'flex-start' : 'center',
      flexDirection: screens.xs ? 'column' : 'row',
      gap: screens.xs ? '12px' : '0',
      marginBottom:'24px', 
      backgroundColor:'#fff', 
      padding: screens.xs ? '16px' : '20px', 
      borderRadius:'8px', 
      boxShadow:'0 2px 8px rgba(0,0,0,0.1)' 
    }}>
      <div>
        <Title level={screens.xs ? 4 : 2} style={{ margin:0 }}>
          <FolderOutlined style={{ marginRight:8, color:'#1890ff' }} />
          {resolvedSelectedProject ? (resolvedSelectedProject.pjtNm || '프로젝트') : '프로젝트 관리'}
        </Title>
        <Text type="secondary" style={{ fontSize: screens.xs ? '13px' : undefined }}>
          {resolvedSelectedProject ? '프로젝트 상세' : `전체 프로젝트 ${projects.length}개`}
        </Text>
      </div>
      {resolvedSelectedProject ? (
        <Button onClick={handleCloseDetail} size={screens.xs ? 'middle' : 'large'}>닫기</Button>
      ) : (
        <Space size={screens.xs ? 4 : 8} wrap>
          <PermissionButton icon={<ReloadOutlined />} onClick={fetchProjects} require={getProjectList} size={screens.xs ? 'small' : 'middle'}>
            {screens.xs ? '새로고침' : '새로고침'}
          </PermissionButton>
          <PermissionButton 
            type="primary" 
            icon={<PlusOutlined />} 
            size={screens.xs ? 'middle' : 'large'} 
            onClick={() => setIsCreateModalOpen(true)} 
            require={createProject}
          >
            {screens.xs ? '프로젝트' : '새 프로젝트'}
          </PermissionButton>
        </Space>
      )}
    </div>
  );

  return (
    <div style={{ backgroundColor:'#f5f5f5', minHeight:'100vh' }}>
      {HeaderSection}
      <PermissionGate require={getProjectList}>
        {({allowed}) => (
          !resolvedSelectedProject ? (
            <>
              {projects.length === 0 ? (
                <Empty description="등록된 프로젝트가 없습니다" style={{ backgroundColor:'#fff', padding:'60px', borderRadius:'8px', boxShadow:'0 2px 8px rgba(0,0,0,0.1)' }} />
              ) : (
                <div style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
                  <Row gutter={[16,16]}>
                    {projects.map(project => (
                      <Col xs={24} sm={12} md={8} lg={6} key={project.pjtId}>
                        <Card
                          hoverable
                          loading={loading}
                          onClick={() => handleOpenDetail(project)}
                          style={{ height:280, borderRadius:12, overflow:'hidden', transition:'all .3s', cursor:'pointer' }}
                          styles={{ body:{ padding:20, height:'100%', display:'flex', flexDirection:'column' } }}
                          actions={[
                            <PermissionGate require={updateProject} key="edit-tt">
                              {({allowed}) => allowed ? (
                                <Tooltip title={'수정'}>
                                  <span role="button" onClick={(e)=>{ e.stopPropagation(); if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; } setSelectedProject(project); setIsEditModalOpen(true); }} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:'100%' }}>
                                    <EditOutlined style={{ fontSize:16 }} />
                                  </span>
                                </Tooltip>
                              ) : null}
                            </PermissionGate>,
                            <PermissionGate require={deleteProject} key="del-tt">
                              {({allowed}) => allowed ? (
                                <Tooltip title={'삭제'}>
                                  <span role="button" onClick={(e)=>{ e.stopPropagation(); handleDelete(project.pjtId, project.pjtNm); }} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:'100%' }}>
                                    <DeleteOutlined style={{ fontSize:16, color:'#ff4d4f' }} />
                                  </span>
                                </Tooltip>
                              ) : null}
                            </PermissionGate>
                          ]}
                        >
                          <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
                            <Title level={4} style={{ margin:'0 0 8px 0', color:'#262626', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {project.pjtNm || '프로젝트명 없음'}
                            </Title>
                            <Text type="secondary" style={{ flex:1, marginBottom:16, display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden', fontSize:13, lineHeight:1.5 }}>
                              {project.pjtInfo || '프로젝트 정보가 없습니다.'}
                            </Text>
                            <Space direction="vertical" size={8} style={{ width:'100%' }}>
                              {project.pjtNo && (
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                  <Text type="secondary" style={{ fontSize:12 }}>프로젝트 번호</Text>
                                  <Tag>{project.pjtNo}</Tag>
                                </div>
                              )}
                              {project.regDt && (
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                  <Text type="secondary" style={{ fontSize:12 }}><CalendarOutlined style={{ marginRight:4 }} />생성일</Text>
                                  <Text style={{ fontSize:12 }}>{new Date(project.regDt).toLocaleDateString()}</Text>
                                </div>
                              )}
                              {project.rgtr && (
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                  <Text type="secondary" style={{ fontSize:12 }}><UserOutlined style={{ marginRight:4 }} />작성자</Text>
                                  <Text style={{ fontSize:12 }}>{project.rgtr}</Text>
                                </div>
                              )}
                            </Space>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </>
          ) : (
            <div style={{ backgroundColor:'#fff', padding:16, borderRadius:8, opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
              <ProjectTabs
                project={resolvedSelectedProject}
                pjtId={resolvedSelectedProject?.pjtId}
                onEdit={() => { setIsEditModalOpen(true); }}
                onDelete={(p) => handleDelete(p.pjtId, p.pjtNm)}
              />
            </div>
          )
        )}
      </PermissionGate>

      <ProjectCreateModal open={isCreateModalOpen} onCancel={() => setIsCreateModalOpen(false)} onSuccess={handleCreateSuccess} />
      <ProjectEditModal open={isEditModalOpen} project={resolvedSelectedProject} onCancel={() => setIsEditModalOpen(false)} onSuccess={handleEditSuccess} />
    </div>
  );
};

export default ProjectList;
